import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import PlaceCard from "./PlaceCard";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Place {
    place_id: string;
    name: string;
    avg_rating: number;
    rate: number;
    address?: string;
    opening_hours?: string;
    description?: string;
    categories?: { id: number; name: string }[];
    images?: { file_url: string }[];
    review_count: number;
}

interface NormalizedPlace {
    id: string;
    name: string;
    rate: number;
    image_urls: string[];
    address?: string;
    opening_hours?: string;
    description?: string;
    review_count : number;
}

interface PlacesSectionProps {
    title: string;
    category: string;
    viewAllLink: string;
}

// کامپوننت اسکلتون با ابعاد دقیق کارت
const PlaceCardSkeleton: React.FC = () => {
  return (
    <div className="w-64 flex-shrink-0 bg-white border border-gray rounded-lg overflow-hidden">
      {/* بخش تصویر: دقیقا h-48 مثل کارت اصلی */}
      <div className="w-full h-48 bg-gray-200 animate-pulse"></div>
      
      {/* بخش محتوا: دقیقا p-3 مثل کارت اصلی */}
      <div className="p-3">
        {/* ردیف عنوان و امتیاز */}
        <div className="flex justify-between items-center mb-1">
          {/* اسکلتون عنوان */}
          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
          {/* اسکلتون امتیاز */}
          <div className="h-4 bg-gray-200 rounded w-8 animate-pulse"></div>
        </div>
        
        {/* اسکلتون آیکون ساعت */}
        <div className="flex items-center h-4 mt-1">
          <div className="h-3 w-3 bg-gray-200 rounded-full ml-1 animate-pulse"></div>
          <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse"></div>
        </div>

        {/* اسکلتون آیکون آدرس */}
        <div className="flex items-center h-4 mt-1">
          <div className="h-3 w-3 bg-gray-200 rounded-full ml-1 animate-pulse"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
        </div>

        {/* اسکلتون توضیحات */}
        <div className="mt-2 space-y-1">
          <div className="h-2 bg-gray-200 rounded w-full animate-pulse"></div>
          <div className="h-2 bg-gray-200 rounded w-full animate-pulse"></div>
          <div className="h-2 bg-gray-200 rounded w-2/3 animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

const PlacesSection: React.FC<PlacesSectionProps> = ({
    title,
    category,
}) => {
    const desktopScrollRef = useRef<HTMLDivElement>(null);
    const mobileScrollRef = useRef<HTMLDivElement>(null);

    /* ---------------- FETCH ---------------- */
    const fetchPlaces = async (): Promise<NormalizedPlace[]> => {
        const res = await fetch(
            `${BASE_URL}/api/places/?category=${encodeURIComponent(category)}&page=1&page_size=10`
        );
        if (!res.ok) throw new Error(`خطا در دریافت ${title}`);
        const json = await res.json();
        const dataArray: Place[] = Array.isArray(json) ? json : json.data || [];
        return dataArray.map((item) => ({
            id: item.place_id,
            name: item.name,
            rate: item.rate ?? 0,
            image_urls: item.images?.map((img) => img.file_url) || [],
            address: item.address,
            opening_hours: item.opening_hours,
            description: item.description,
            review_count : item.review_count,
        }));
    };

    const { data: places = [], isLoading, isError, error } = useQuery({
        queryKey: ["places", category],
        queryFn: fetchPlaces,
        staleTime: 5 * 60 * 1000,
    });

    /* ---------------- SCROLL ---------------- */
    const scrollPrev = () =>
        desktopScrollRef.current?.scrollBy({ left: 600, behavior: "smooth" });
    const scrollNext = () =>
        desktopScrollRef.current?.scrollBy({ left: -600, behavior: "smooth" });

    /* ---------------- RENDER ---------------- */
    return (
        <div className="py-1 px-0 desktop:px-16">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 desktop:px-20 tablet:px-20 px-[16px]">
                <h2 className="text-xl tablet:text-2xl desktop:text-3xl font-myYekanDemibold">
                    {title}
                </h2>
                <Link
                    to={`/places/category/${encodeURIComponent(category)}`}
                    className="flex items-center gap-1 text-sm text-gray-800 font-myYekanFaNumMedium"
                >
                    مشاهده همه
                    <ChevronLeftIcon className="h-4 w-4" />
                </Link>
            </div>

            {/* ---------- Mobile ---------- */}
            <div className="md:hidden">
                <div
                    ref={mobileScrollRef}
                    className="flex overflow-x-auto space-x-2 rtl:space-x-reverse px-2 pb-6 scrollbar-hide"
                >
                    {isLoading ? (
                        // نمایش اسکلتون در حالت لودینگ موبایل
                        Array.from({ length: 5 }).map((_, i) => (
                            <PlaceCardSkeleton key={i} />
                        ))
                    ) : isError ? (
                        // نمایش پیام خطا در موبایل
                        <div className="w-full text-center text-red-500 py-10">
                            {(error as Error).message}
                        </div>
                    ) : (
                        // نمایش داده‌ها
                        places.map((place) => (
                            <PlaceCard key={place.id} place={place} />
                        ))
                    )}
                </div>
            </div>

            {/* ---------- Desktop ---------- */}
            <div className="hidden md:block relative px-12">
                <div className="flex items-center">
                    <button onClick={scrollPrev} className="w-10 h-10 flex items-center justify-center">
                        <ChevronRightIcon className="h-6 w-6" />
                    </button>
                    <div
                        ref={desktopScrollRef}
                        className="flex overflow-x-auto space-x-2 rtl:space-x-reverse pb-6 flex-1 scrollbar-hide"
                    >
                        {isLoading ? (
                            // نمایش اسکلتون در حالت لودینگ دسکتاپ
                            Array.from({ length: 5 }).map((_, i) => (
                                <PlaceCardSkeleton key={i} />
                            ))
                        ) : isError ? (
                            // نمایش پیام خطا در دسکتاپ
                            <div className="w-full text-center text-red-500 py-10">
                                {(error as Error).message}
                            </div>
                        ) : (
                            // نمایش داده‌ها
                            places.map((place) => (
                                <PlaceCard key={place.id} place={place} />
                            ))
                        )}
                    </div>
                    <button onClick={scrollNext} className="w-10 h-10 flex items-center justify-center">
                        <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                </div>
            </div>
            <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { scrollbar-width: none; }
      `}</style>
        </div>
    );
};

export default PlacesSection;